import React, { useState, useEffect, useCallback } from 'react';
import { FiRefreshCw, FiSearch, FiX, FiAlertTriangle, FiDollarSign, FiEye } from 'react-icons/fi';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { fmtFecha, fmtMoney, localDate } from '../utils/format';
import { showToast } from '../utils/toast';
import '../styles/Compras.css';
import '../styles/CuentasCobrar.css';

/* ── Helpers ────────────────────────────────────────────────────── */
const fmt = (n) => parseFloat(n || 0).toFixed(2);
const today = () => localDate();

const isVencida = (row) =>
  ['PENDIENTE', 'PARCIAL'].includes(row.estado) &&
  row.fecha_vencimiento &&
  String(row.fecha_vencimiento).slice(0, 10) < today();

/* ── Componente ──────────────────────────────────────────────────── */
export default function CuentasCobrarPage() {
  const { user } = useAuth();
  const bizId = user?.business_id || 1;

  /* ── Listado ── */
  const [rows, setRows]     = useState([]);
  const [total, setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [summary, setSummary] = useState({});

  /* ── Filtros (estado interno del formulario) ── */
  const [search,       setSearch]       = useState('');
  const [fDesde,       setFDesde]       = useState(today());
  const [fHasta,       setFHasta]       = useState(today());
  const [fEstado,      setFEstado]      = useState('');
  const [fCliente,     setFCliente]     = useState('');
  const [soloVencidas, setSoloVencidas] = useState(false);

  /* Filtros aplicados (solo cambian al presionar Buscar / Enter) */
  const [applied, setApplied] = useState({
    search: '', fDesde: today(), fHasta: today(), fEstado: '', fCliente: '', soloVencidas: false,
  });

  /* ── Catálogos ── */
  const [clientes,   setClientes]   = useState([]);
  const [formasPago, setFormasPago] = useState([]);

  /* ── Modal detalle / cobro ── */
  const [modalCxC,     setModalCxC]    = useState(null);
  const [showCobroForm, setShowCobroForm] = useState(false);
  const [cobroForm, setCobroForm] = useState({
    fecha: today(),
    forma_pago: '',
    monto: '',
    referencia: '',
  });
  const [cobrandoError, setCobrandoError] = useState('');
  const [cobrando,      setCobrando]      = useState(false);

  /* ── Carga de resumen KPIs ── */
  const loadSummary = useCallback(async () => {
    try {
      const r = await api.get('/api/cuentas-cobrar/summary');
      setSummary(r.data.data || {});
    } catch { /* silencioso */ }
  }, []);

  /* ── Carga principal de listado ── */
  const loadList = useCallback(async (filters) => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get('/api/cuentas-cobrar', {
        params: {
          q:            filters.search,
          desde:        filters.fDesde,
          hasta:        filters.fHasta,
          estado:       filters.fEstado,
          cliente_id:   filters.fCliente,
          solo_vencidas: filters.soloVencidas ? 'true' : '',
        },
      });
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  /* Carga inicial */
  useEffect(() => {
    loadList(applied);
    loadSummary();
  }, [loadList, loadSummary]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Catálogos (una sola vez) */
  useEffect(() => {
    api.get('/api/clientes')
      .then(r => setClientes(r.data.data || []))
      .catch(() => {});
    api.get(`/api/config/formas-pago?business_id=${bizId}`)
      .then(r => setFormasPago(r.data.data || []))
      .catch(() => {});
  }, [bizId]);

  /* ── Aplicar filtros ── */
  const applyFilters = useCallback(() => {
    const next = { search, fDesde, fHasta, fEstado, fCliente, soloVencidas };
    setApplied(next);
    loadList(next);
    loadSummary();
  }, [search, fDesde, fHasta, fEstado, fCliente, soloVencidas, loadList, loadSummary]);

  const handleSearchKey = (e) => {
    if (e.key === 'Enter') applyFilters();
  };

  const clearFilters = () => {
    const empty = { search: '', fDesde: today(), fHasta: today(), fEstado: '', fCliente: '', soloVencidas: false };
    setSearch(''); setFDesde(today()); setFHasta(today());
    setFEstado(''); setFCliente(''); setSoloVencidas(false);
    setApplied(empty);
    loadList(empty);
    loadSummary();
  };

  const hasFilters = search || fDesde || fHasta || fEstado || fCliente || soloVencidas;

  /* ── Abrir modal de detalle ── */
  const openDetalle = async (id, abrirFormulario = false) => {
    setCobrandoError('');
    setShowCobroForm(false);
    setCobroForm({ fecha: today(), forma_pago: '', monto: '', referencia: '' });
    try {
      const r = await api.get(`/api/cuentas-cobrar/${id}`);
      const data = r.data.data;
      setModalCxC(data);
      if (abrirFormulario && ['PENDIENTE', 'PARCIAL'].includes(data.estado)) {
        const restante = parseFloat(data.monto || 0) - parseFloat(data.monto_cobrado || 0);
        setCobroForm(f => ({ ...f, monto: fmt(restante) }));
        setShowCobroForm(true);
      }
    } catch (e) {
      showToast(e.response?.data?.error || e.message);
    }
  };

  /* ── Registrar cobro ── */
  const handleCobrar = async () => {
    if (!cobroForm.monto || parseFloat(cobroForm.monto) <= 0) {
      setCobrandoError('Ingrese un monto válido.');
      return;
    }
    if (!cobroForm.forma_pago) {
      setCobrandoError('Seleccione la forma de pago.');
      return;
    }
    setCobrando(true);
    setCobrandoError('');
    try {
      await api.post(`/api/cuentas-cobrar/${modalCxC.id}/cobrar`, cobroForm);
      /* Recargar detalle */
      const r = await api.get(`/api/cuentas-cobrar/${modalCxC.id}`);
      const data = r.data.data;
      setModalCxC(data);
      setShowCobroForm(false);
      setCobroForm({ fecha: today(), forma_pago: '', monto: '', referencia: '' });
      loadList(applied);
      loadSummary();
      showToast('Cobro registrado correctamente');
    } catch (e) {
      setCobrandoError(e.response?.data?.error || e.message);
    } finally {
      setCobrando(false);
    }
  };

  /* ── Utilidades del modal ── */
  const restante = modalCxC
    ? Math.max(0, parseFloat(modalCxC.monto || 0) - parseFloat(modalCxC.monto_cobrado || 0))
    : 0;

  const pctCobrado = modalCxC
    ? Math.min(100, (parseFloat(modalCxC.monto_cobrado || 0) / Math.max(0.01, parseFloat(modalCxC.monto || 1))) * 100)
    : 0;

  const progressClass =
    pctCobrado >= 100 ? 'cxc-progress-fill' :
    pctCobrado >= 50  ? 'cxc-progress-fill warn' :
                        'cxc-progress-fill danger';

  const estadoBadgeClass = (estado, row) => {
    if (isVencida(row)) return 'cxc-badge cxc-badge-venc';
    const map = {
      PENDIENTE: 'cxc-badge cxc-badge-pend',
      PARCIAL:   'cxc-badge cxc-badge-parc',
      COBRADO:   'cxc-badge cxc-badge-cobr',
    };
    return map[estado] || 'cxc-badge cxc-badge-pend';
  };

  const estadoBadgeLabel = (estado, row) => isVencida(row) ? 'VENCIDA' : estado;

  /* ── Render ── */
  return (
    <div className="cxc-page">

      {/* ── Toolbar ── */}
      <div className="cxc-toolbar">
        <span className="cxc-toolbar-title">Cuentas por Cobrar</span>
        <button
          className="cxc-btn-refresh"
          onClick={() => { loadList(applied); loadSummary(); }}
        >
          <FiRefreshCw size={13} /> Actualizar
        </button>
      </div>

      {/* ── KPI Cards ── */}
      <div className="cxc-kpis">
        <div className="cxc-kpi-card cobrar">
          <div className="cxc-kpi-label">Por Cobrar</div>
          <div className="cxc-kpi-value blue">
            S/ {fmtMoney(summary.total_pendiente)}
          </div>
        </div>
        <div className="cxc-kpi-card vencido">
          <div className="cxc-kpi-label">Vencido</div>
          <div className={`cxc-kpi-value ${parseFloat(summary.total_vencido) > 0 ? 'red' : ''}`}>
            S/ {fmtMoney(summary.total_vencido)}
          </div>
        </div>
        <div className="cxc-kpi-card pendiente">
          <div className="cxc-kpi-label">Pendientes</div>
          <div className="cxc-kpi-value orange">{summary.count_pendiente || 0}</div>
        </div>
        <div className="cxc-kpi-card cobrado">
          <div className="cxc-kpi-label">Cobrado</div>
          <div className="cxc-kpi-value green">
            S/ {fmtMoney(summary.total_cobrado)}
          </div>
        </div>
      </div>

      {/* ── Filtros ── */}
      <div className="cxc-filtros">
        <div className="cxc-search">
          <FiSearch size={13} color="#94a3b8" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={handleSearchKey}
            placeholder="Cliente, N° venta, DNI…"
          />
        </div>

        <input
          type="date"
          className="cxc-date-input"
          value={fDesde}
          onChange={e => setFDesde(e.target.value)}
          title="Fecha desde"
        />
        <span className="cxc-filter-sep">—</span>
        <input
          type="date"
          className="cxc-date-input"
          value={fHasta}
          onChange={e => setFHasta(e.target.value)}
          title="Fecha hasta"
        />

        <select
          className="cxc-select"
          value={fEstado}
          onChange={e => setFEstado(e.target.value)}
        >
          <option value="">Todos los estados</option>
          <option value="PENDIENTE">Pendiente</option>
          <option value="PARCIAL">Parcial</option>
          <option value="COBRADO">Cobrado</option>
        </select>

        <select
          className="cxc-select"
          value={fCliente}
          onChange={e => setFCliente(e.target.value)}
        >
          <option value="">Todos los clientes</option>
          {clientes.map(c => (
            <option key={c.id} value={c.id}>{c.nombre}</option>
          ))}
        </select>

        <label className="cxc-toggle-label">
          <input
            type="checkbox"
            checked={soloVencidas}
            onChange={e => setSoloVencidas(e.target.checked)}
          />
          Solo vencidas
        </label>

        <button className="cxc-btn-search" onClick={applyFilters}>
          <FiSearch size={12} /> Buscar
        </button>

        {hasFilters && (
          <button className="cxc-btn-clear" onClick={clearFilters}>
            <FiX size={12} /> Limpiar
          </button>
        )}
      </div>

      {/* ── Tabla ── */}
      <div className="cxc-table-wrap">
        {loading ? (
          <div className="cxc-empty">Cargando…</div>
        ) : error ? (
          <div className="cxc-error">{error}</div>
        ) : rows.length === 0 ? (
          <div className="cxc-empty">No hay cuentas por cobrar con los filtros seleccionados.</div>
        ) : (
          <table className="cxc-table">
            <thead>
              <tr>
                <th>N° Venta</th>
                <th>Fecha</th>
                <th>Cliente</th>
                <th>DNI / Doc.</th>
                <th>Vencimiento</th>
                <th style={{ textAlign: 'right' }}>Monto</th>
                <th style={{ textAlign: 'right' }}>Cobrado</th>
                <th style={{ textAlign: 'right' }}>Restante</th>
                <th>Estado</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const vencida  = isVencida(row);
                const restRow  = parseFloat(row.monto || 0) - parseFloat(row.monto_cobrado || 0);
                const puedeActuar = ['PENDIENTE', 'PARCIAL'].includes(row.estado);
                return (
                  <tr key={row.id}>
                    <td><strong>{row.venta_numero || '—'}</strong></td>
                    <td>{fmtFecha(row.venta_fecha)}</td>
                    <td>
                      <div>{row.cliente_nombre || '—'}</div>
                      {row.cliente_telefono && (
                        <small style={{ color: '#64748b' }}>{row.cliente_telefono}</small>
                      )}
                    </td>
                    <td>{row.cliente_documento || '—'}</td>
                    <td>
                      {vencida ? (
                        <span className="cxc-vencida">
                          <FiAlertTriangle size={11} style={{ marginRight: 3, verticalAlign: 'middle' }} />
                          {fmtFecha(row.fecha_vencimiento)}
                        </span>
                      ) : (
                        fmtFecha(row.fecha_vencimiento)
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>S/ {fmt(row.monto)}</td>
                    <td style={{ textAlign: 'right', color: '#15803d' }}>
                      S/ {fmt(row.monto_cobrado)}
                    </td>
                    <td style={{
                      textAlign: 'right',
                      fontWeight: 700,
                      color: restRow > 0 ? '#ef4444' : '#94a3b8',
                    }}>
                      S/ {fmt(restRow)}
                    </td>
                    <td>
                      <span className={estadoBadgeClass(row.estado, row)}>
                        {estadoBadgeLabel(row.estado, row)}
                      </span>
                    </td>
                    <td>
                      <div className="cxc-row-actions">
                        {puedeActuar ? (
                          <button
                            className="cxc-btn-cobrar"
                            onClick={() => openDetalle(row.id, true)}
                            title="Ver detalle y cobrar"
                          >
                            <FiDollarSign size={12} /> Ver / Cobrar
                          </button>
                        ) : (
                          <button
                            className="cxc-btn-ver"
                            onClick={() => openDetalle(row.id, false)}
                            title="Ver detalle"
                          >
                            <FiEye size={12} /> Ver
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Totalizador ── */}
      <div className="cxc-total-bar">
        <span>{total} cuenta{total !== 1 ? 's' : ''}</span>
        <span>Mostrando: <strong>{rows.length}</strong></span>
      </div>

      {/* ── MODAL DETALLE / COBRO ── */}
      {modalCxC && (
        <div className="cxc-overlay">
          <div className="cxc-modal">

            {/* Header */}
            <div className="cxc-modal-header">
              <h3>Cuenta por Cobrar — {modalCxC.venta_numero || `#${modalCxC.id}`}</h3>
              <button className="cxc-modal-close" onClick={() => setModalCxC(null)}>×</button>
            </div>

            {/* Barra de resumen económico */}
            <div className="cxc-saldo-bar">
              <div className="saldo-item">
                <span className="saldo-label">Monto Total</span>
                <span className="saldo-value">S/ {fmt(modalCxC.monto)}</span>
              </div>
              <div className="saldo-item">
                <span className="saldo-label">Cobrado</span>
                <span className="saldo-value success">S/ {fmt(modalCxC.monto_cobrado)}</span>
              </div>
              <div className="saldo-item">
                <span className="saldo-label">Restante</span>
                <span className={`saldo-value ${restante > 0 ? 'danger' : ''}`}>
                  S/ {fmt(restante)}
                </span>
              </div>
              <div className="saldo-item">
                <span className="saldo-label">Vencimiento</span>
                <span className={`saldo-value ${isVencida(modalCxC) ? 'danger' : ''}`}>
                  {fmtFecha(modalCxC.fecha_vencimiento)}
                </span>
              </div>
              <div className="saldo-item">
                <span className="saldo-label">Estado</span>
                <span className={estadoBadgeClass(modalCxC.estado, modalCxC)}>
                  {estadoBadgeLabel(modalCxC.estado, modalCxC)}
                </span>
              </div>
            </div>

            {/* Barra de progreso */}
            <div className="cxc-progress-wrap">
              <div className="cxc-progress-label">
                <span>Progreso de cobro</span>
                <span>{pctCobrado.toFixed(1)}%</span>
              </div>
              <div className="cxc-progress-bar">
                <div
                  className={progressClass}
                  style={{ width: `${pctCobrado}%` }}
                />
              </div>
            </div>

            {/* Cuerpo del modal */}
            <div className="cxc-modal-body">

              {/* Información del cliente */}
              <div className="cxc-section">
                <h4>Cliente</h4>
                <div className="cxc-info-grid">
                  <span className="key">Nombre</span>
                  <span className="val">{modalCxC.cliente_nombre || '—'}</span>
                  <span className="key">Documento</span>
                  <span className="val">{modalCxC.cliente_documento || '—'}</span>
                  {modalCxC.cliente_telefono && (
                    <>
                      <span className="key">Teléfono</span>
                      <span className="val">{modalCxC.cliente_telefono}</span>
                    </>
                  )}
                  <span className="key">N° Venta</span>
                  <span className="val">
                    {modalCxC.venta_numero || '—'}
                    {modalCxC.venta_fecha ? ` — ${fmtFecha(modalCxC.venta_fecha)}` : ''}
                  </span>
                </div>
              </div>

              {/* Historial de cobros */}
              <div className="cxc-section">
                <h4>Historial de cobros</h4>
                {(modalCxC.cobros || []).length === 0 ? (
                  <div className="cxc-empty" style={{ padding: '12px 0' }}>
                    Sin cobros registrados
                  </div>
                ) : (
                  <table className="cxc-cobros-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Forma de Pago</th>
                        <th>Referencia</th>
                        <th>Usuario</th>
                        <th style={{ textAlign: 'right' }}>Monto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalCxC.cobros.map(c => (
                        <tr key={c.id}>
                          <td>{fmtFecha(c.cobrado_en)}</td>
                          <td>{c.forma_pago || '—'}</td>
                          <td>{c.referencia || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                          <td>{c.usuario_nombre || '—'}</td>
                          <td className="monto-cell">S/ {fmt(c.monto)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Formulario de nuevo cobro (solo si pendiente/parcial) */}
              {['PENDIENTE', 'PARCIAL'].includes(modalCxC.estado) && (
                <div className="cxc-section">
                  <h4>Registrar Cobro</h4>

                  {!showCobroForm ? (
                    <button
                      className="cxc-btn-open-form"
                      onClick={() => {
                        setCobroForm(f => ({ ...f, monto: fmt(restante) }));
                        setCobrandoError('');
                        setShowCobroForm(true);
                      }}
                    >
                      <FiDollarSign size={13} /> Registrar Cobro
                    </button>
                  ) : (
                    <div className="cxc-cobro-form">
                      {cobrandoError && (
                        <div className="cxc-error">{cobrandoError}</div>
                      )}
                      <div className="cxc-cobro-form-grid">

                        <div className="cxc-form-field">
                          <label>Fecha</label>
                          <input
                            type="date"
                            value={cobroForm.fecha}
                            onChange={e => setCobroForm(f => ({ ...f, fecha: e.target.value }))}
                          />
                        </div>

                        <div className="cxc-form-field">
                          <label>Forma de Pago</label>
                          <select
                            value={cobroForm.forma_pago}
                            onChange={e => setCobroForm(f => ({ ...f, forma_pago: e.target.value }))}
                          >
                            <option value="">— Seleccionar —</option>
                            {formasPago.map(fp => (
                              <option key={fp.id} value={fp.nombre || fp.name}>
                                {fp.nombre || fp.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="cxc-form-field">
                          <label>Monto (Restante: S/ {fmt(restante)})</label>
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            max={restante}
                            value={cobroForm.monto}
                            onChange={e => setCobroForm(f => ({ ...f, monto: e.target.value }))}
                            placeholder={fmt(restante)}
                          />
                        </div>

                        <div className="cxc-form-field">
                          <label>Referencia / N° Operación</label>
                          <input
                            type="text"
                            value={cobroForm.referencia}
                            onChange={e => setCobroForm(f => ({ ...f, referencia: e.target.value }))}
                            placeholder="Opcional…"
                          />
                        </div>

                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer del modal */}
            <div className="cxc-modal-footer">
              <button
                className="cxc-btn-cancel"
                onClick={() => setModalCxC(null)}
              >
                Cerrar
              </button>
              {showCobroForm && ['PENDIENTE', 'PARCIAL'].includes(modalCxC.estado) && (
                <button
                  className="cxc-btn-registrar"
                  onClick={handleCobrar}
                  disabled={cobrando}
                >
                  {cobrando ? 'Registrando…' : 'Confirmar Cobro'}
                </button>
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
